import { useState, useEffect, useCallback } from 'react';
import { money } from '../events/util';

const API = process.env.REACT_APP_API_URL || '';

// PEDIR SERVICIO: el cliente pide desde su mesa y una moza se lo lleva.
//
// El precio y el total los calcula el SERVIDOR; acá se muestran solo para que la persona
// sepa qué va a gastar. Lo que se manda son productos y cantidades.
//
// El pago mixto se explica ANTES de confirmar: si el saldo no alcanza, tiene que quedar
// claro cuánto va a pagar en mano, o la moza llega a la mesa con una sorpresa.
export default function PedirServicio({ code, token, mesa, onSaldo }) {
  const [abierto, setAbierto] = useState(false);
  const [menu, setMenu] = useState(null);       // null = sin cargar
  const [cant, setCant] = useState({});          // { productId: qty }
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState('');
  const [pedidos, setPedidos] = useState([]);

  const cargarPedidos = useCallback(() => {
    fetch(`${API}/api/public/mesa/${code}/pedidos?token=${encodeURIComponent(token || '')}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setPedidos(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [code, token]);

  // Los pedidos en curso se refrescan solos: la persona quiere ver que alguien lo tomó.
  // 20 s es suficiente para eso y no castiga la red de la noche.
  useEffect(() => {
    cargarPedidos();
    const id = setInterval(cargarPedidos, 20000);
    return () => clearInterval(id);
  }, [cargarPedidos]);

  const abrir = () => {
    setAbierto(true); setErr('');
    if (menu === null) {
      fetch(`${API}/api/public/mesa/${code}/menu`)
        .then(r => r.json()).then(setMenu)
        .catch(() => setMenu({ disponible: false, motivo: 'No se pudo cargar el menú.' }));
    }
  };

  const set = (id, q) => setCant(c => ({ ...c, [id]: Math.max(0, Math.min(20, q)) }));
  const productos = menu?.productos || [];
  const elegidos = productos.filter(p => cant[p.id] > 0);
  const total = elegidos.reduce((a, p) => a + Number(p.price) * cant[p.id], 0);
  const saldo = Number(mesa?.consumo_remaining != null ? mesa.consumo_remaining : mesa?.consumo_total) || 0;
  const conSaldo = Math.min(saldo, total);
  const enMano = total - conSaldo;

  async function confirmar() {
    setErr(''); setEnviando(true);
    try {
      const r = await fetch(`${API}/api/public/mesa/${code}/pedido`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, nota, items: elegidos.map(p => ({ id: p.id, qty: cant[p.id] })) }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || 'No se pudo enviar el pedido'); return; }
      setCant({}); setNota(''); setAbierto(false);
      cargarPedidos();
      onSaldo && onSaldo();
    } catch {
      // Sin conexión no hay pedido posible: el circuito necesita llegar a la moza. Se
      // dice qué hacer en su lugar en vez de dejar un botón que no responde.
      setErr('Sin conexión. Podés acercarte a la barra con tu código.');
    } finally { setEnviando(false); }
  }

  const enCurso = pedidos.filter(p => p.status === 'pendiente' || p.status === 'tomado');

  return (
    <div style={{ marginTop: 18 }}>
      {!abierto && (
        <button className="lb-btn lb-btn--white lb-btn--block" onClick={abrir}>🛎️ Pedir servicio a la mesa</button>
      )}

      {enCurso.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {enCurso.map(p => (
            <div key={p.id} style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 8, textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>Pedido #{p.numero}</span>
                <span style={{ fontSize: 11.5, color: p.status === 'tomado' ? '#2ecc71' : '#ffb300' }}>
                  {p.status === 'tomado' ? `${p.moza || 'Una moza'} te lo está llevando` : 'Esperando que lo tome una moza'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 4 }}>
                {(p.items || []).map(i => `${i.qty}× ${i.name}`).join(' · ')}
              </div>
              {Number(p.a_cobrar) > 0 && (
                <div style={{ fontSize: 12, color: '#fff', marginTop: 4 }}>Pagás <b>{money(p.a_cobrar)}</b> al recibirlo</div>
              )}
            </div>
          ))}
        </div>
      )}

      {abierto && (
        <div style={{ marginTop: 12, padding: '16px 16px 18px', border: '1px solid var(--border)', borderRadius: 8, textAlign: 'left' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div className="lb-eyebrow">Pedir a la mesa</div>
            <button className="lb-iconbtn" onClick={() => setAbierto(false)}>✕</button>
          </div>

          {menu === null && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Cargando el menú…</div>}
          {menu && !menu.disponible && <div style={{ color: 'var(--ink-700)', fontSize: 13, lineHeight: 1.5 }}>{menu.motivo}</div>}

          {menu?.disponible && (
            <>
              {Object.entries(productos.reduce((acc, p) => {
                (acc[p.category || 'General'] = acc[p.category || 'General'] || []).push(p); return acc;
              }, {})).map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-600)', marginBottom: 6 }}>{cat}</div>
                  {items.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#fff', fontSize: 13.5 }}>{p.name}</div>
                        <div style={{ color: 'var(--ink-600)', fontSize: 12 }}>{money(p.price)}</div>
                      </div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 999 }}>
                        <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => set(p.id, (cant[p.id] || 0) - 1)}>–</button>
                        <span style={{ minWidth: 26, textAlign: 'center', fontWeight: 600 }}>{cant[p.id] || 0}</span>
                        <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => set(p.id, (cant[p.id] || 0) + 1)}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <input className="lb-input" value={nota} onChange={e => setNota(e.target.value)}
                placeholder="Aclaración (opcional): sin hielo, dos vasos…" style={{ marginTop: 4 }} />

              {total > 0 && (
                <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--ink-050)', borderRadius: 6 }}>
                  <Linea t="Total del pedido" v={money(total)} fuerte />
                  {conSaldo > 0 && <Linea t="Se descuenta de tu consumo" v={`− ${money(conSaldo)}`} verde />}
                  {enMano > 0 && <Linea t="Pagás al recibirlo" v={money(enMano)} fuerte />}
                  {enMano > 0 && saldo > 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 6, lineHeight: 1.45 }}>
                      Tu saldo no alcanza para todo el pedido: la diferencia se la pagás a la moza cuando te lo lleve.
                    </div>
                  )}
                </div>
              )}

              {err && <div className="lb-err" style={{ marginTop: 10 }}>{err}</div>}
              <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 12 }}
                disabled={!elegidos.length || enviando} onClick={confirmar}>
                {enviando ? 'Enviando…' : elegidos.length ? `Pedir · ${money(total)}` : 'Elegí algo para pedir'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Linea({ t, v, fuerte, verde }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0' }}>
      <span style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>{t}</span>
      <span style={{ fontSize: fuerte ? 15 : 13, fontWeight: fuerte ? 700 : 500, color: verde ? '#2ecc71' : '#fff' }}>{v}</span>
    </div>
  );
}
